/**
 * apk-progress.util — turns the server's phase model into what the Development card draws.
 *
 * Spec: docs/superpowers/specs/2026-09-04-apk-build-progress-design.md
 *
 * TWO RULES, AND THEY ARE THE WHOLE SAFETY STORY:
 *   1. elapsed time creeps the bar INSIDE the current phase's span and stops at its ceiling;
 *   2. only a real workflow step completing moves the bar past a GATED phase.
 * So the bar can never be more than one gate ahead of the truth. A build wedged in the long Gradle
 * step parks partway along with an unfinished phase showing — honest, and obviously not done.
 *
 * The phase table itself lives on the SERVER and arrives with the payload. Keeping a second copy
 * here would put the two repos' tables one deploy apart, misaligning every span silently.
 */

export interface ApkProgressPhase {
  key: string;
  label: string;
  gated: boolean;
  weight: number;
  endedAt: number | null;
}

export interface ApkProgress {
  runStartedAt: number;
  updatedAt: number;
  phases: ApkProgressPhase[];
}

export interface ApkProgressView {
  percent: number;
  /** At most three: two finished, then the one in progress. */
  visible: { key: string; label: string; done: boolean }[];
  timeLeftLabel: string | null;
  overrunning: boolean;
  /** The estimate for the current phase is spent and we are parked at its gate, waiting for CI to
   *  report. Distinct from `overrunning`, which is about the run as a whole. */
  stalled: boolean;
}

/** Past this, stop estimating and say so. A five-minute build at eight minutes is not slow. */
export const OVERRUN_MS = 8 * 60 * 1000;

function label(secs: number): string {
  if (secs <= 45) return 'A few seconds left';
  const mins = Math.max(1, Math.round(secs / 60));
  return mins === 1 ? 'About a minute left' : `About ${mins} minutes left`;
}

/**
 * @param floorPercent the highest percentage already shown. The bar must never retreat: a rescale
 *        that implies more work left is more accurate, but a bar going backwards reads as a fault.
 */
export function progressView(progress: ApkProgress, now: number, floorPercent: number): ApkProgressView {
  const phases = progress?.phases ?? [];
  if (!phases.length) {
    return { percent: Math.max(0, floorPercent), visible: [], timeLeftLabel: null, overrunning: false, stalled: false };
  }

  const total = phases.reduce((a, p) => a + (p.weight > 0 ? p.weight : 1), 0);
  const cum: number[] = [];
  let acc = 0;
  for (const p of phases) { acc += (p.weight > 0 ? p.weight : 1); cum.push((acc / total) * 100); }

  const doneCount = phases.findIndex((p) => p.endedAt === null);
  if (doneCount === -1) {
    return {
      percent: 100,
      visible: phases.slice(-3).map((p) => ({ key: p.key, label: p.label, done: true })),
      timeLeftLabel: null,
      overrunning: false,
      stalled: false,
    };
  }

  // The furthest the bar may go on time alone: the ceiling of the first gated phase that has not
  // landed. Timed phases before it are traversed freely; the gate is the wall.
  let gateIdx = doneCount;
  while (gateIdx < phases.length && !phases[gateIdx].gated) gateIdx++;
  const gi = Math.min(gateIdx, phases.length - 1);
  const gBase = gi === 0 ? 0 : cum[gi - 1];
  const ceiling = gBase + (cum[gi] - gBase) * 0.99;

  const startedAt = doneCount === 0 ? progress.runStartedAt : (phases[doneCount - 1].endedAt as number);
  const elapsed = Math.max(0, now - startedAt) / 1000;

  // Walk forward from the first unfinished phase, spending elapsed seconds against each weight.
  let idx = doneCount;
  let left = elapsed;
  let pct = doneCount === 0 ? 0 : cum[doneCount - 1];
  let stalled = false;
  while (idx < phases.length) {
    const w = phases[idx].weight > 0 ? phases[idx].weight : 1;
    const base = idx === 0 ? 0 : cum[idx - 1];
    const span = cum[idx] - base;
    if (left < w) { pct = base + (left / w) * span; break; }
    // A gated phase cannot be walked past on time. Park just SHORT of its ceiling, not on it:
    // filling the span exactly renders identically to the phase being finished, and the whole
    // point of a gate is that we do not yet know that it is.
    //
    // Reaching here means the estimate for this phase is SPENT and CI still has not reported. The
    // remaining-time sum below would then say "a few seconds left" forever, which is what a cold
    // Gradle cache actually produced: parked at 95% under "Signing the app" for minutes, still
    // promising seconds. So flag it and stop counting down.
    if (phases[idx].gated) { pct = base + span * 0.99; left = w; stalled = true; break; }
    left -= w;
    pct = cum[idx];
    idx++;
  }

  const activeIdx = Math.min(idx, phases.length - 1);
  const percent = Math.max(floorPercent, Math.min(pct, ceiling, 99.5));

  const visible = [
    ...phases.slice(Math.max(0, activeIdx - 2), activeIdx).map((p) => ({ key: p.key, label: p.label, done: true })),
    { key: phases[activeIdx].key, label: phases[activeIdx].label, done: false },
  ];

  const overrunning = now - progress.runStartedAt > OVERRUN_MS;
  const remaining = phases.slice(activeIdx).reduce((a, p) => a + (p.weight > 0 ? p.weight : 1), 0)
    - Math.min(left, phases[activeIdx].weight);
  // No countdown once the estimate is spent — a number that never decreases is worse than none.
  const timeLeftLabel = overrunning || stalled ? null : label(Math.max(5, remaining));

  return { percent, visible, timeLeftLabel, overrunning, stalled };
}
